import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import CardsContainer from "@/components/CardsContainer";
import CardsList from "@/components/CardsList";
import CardsTable from "@/components/CardsTable";
import VLFModal from "@/components/Modal";

import {PlayerRoleAPI} from "@/api/player_role";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import {
	AREA_LABELS,
	ERROR_OPERATION,
	SUCCESSFUL_OPERATION,
} from "@/utils/constants";

import "./index.scss";

function SettingsPlayerRolesPage(props) {
	const {handleNotification} = props;
	const navigate = useNavigate();
	const admin = useSelector((s) => s.user?.roleName?.toLowerCase() === "admin");

	const [loading, setLoading] = useState(false);
	const [playerRoles, setPlayerRoles] = useState([]);
	const [openHandleConfirm, setOpenHandleConfirm] = useState(false);
	const [selectedId, setSelectedId] = useState(null);

	const getPlayerRoles = async () => {
		setLoading(true);
		try {
			const response = await PlayerRoleAPI.list();

			const AREA_ORDER = {
				Goalkeeper: 1,
				Defense: 2,
				DefensiveMidfield: 3,
				Midfield: 4,
				AttackingMidfield: 5,
				Attack: 6,
			};
			const sorted = (response ?? []).slice().sort((a, b) => {
				const areaDiff =
					(AREA_ORDER[a.area] ?? 99) - (AREA_ORDER[b.area] ?? 99);
				if (areaDiff !== 0) return areaDiff;
				return (a.sorting ?? 0) - (b.sorting ?? 0);
			});
			setPlayerRoles(sorted);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const deletePlayerRole = async () => {
		setLoading(true);
		try {
			await PlayerRoleAPI.delete(selectedId);
			setOpenHandleConfirm(false);
			getPlayerRoles();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	useEffect(() => {
		getPlayerRoles();
	}, []);

	const listConfiguration = {
		data: playerRoles,
		onClick: (role) => navigate("/settings/player-roles/" + role.id),
		title: (role) => role.name,
		subtext: (role) => AREA_LABELS[role.area] || role.area || "",
		actions: [
			{
				icon: <EditIcon />,
				variant: "outlined",
				title: "Modifica",
				onClick: (role) =>
					navigate("/settings/player-roles/" + role.id + "/edit"),
			},
			{
				icon: <DeleteIcon />,
				variant: "outlined",
				title: "Elimina",
				onClick: (role) => {
					setSelectedId(role.id);
					setOpenHandleConfirm(true);
				},
				color: "error",
			},
		],
	};

	const columns = [
		{label: "Codice", key: "code"},
		{label: "Reparto", key: "department"},
		{
			label: "Stato",
			key: "active",
			customRender: (r) => (r.active === false ? "Inattivo" : "Attivo"),
		},
		{label: "Nome", key: "name", align: "start"},
		{
			label: "Ruolo principale",
			key: "area",
			align: "start",
			customRender: (role) => AREA_LABELS[role.area] || role.area || "",
		},
		{label: "Ordinamento", key: "sorting"},
	];

	const mainActions = [
		...(admin
			? [
					{
						label: "Inizializza ruoli mancanti",
						onClick: async () => {
							setLoading(true);
							try {
								await PlayerRoleAPI.defaults();
								await getPlayerRoles();
							} catch {
								handleNotification(ERROR_OPERATION, "error");
							} finally {
								setLoading(false);
							}
						},
					},
				]
			: []),
		{
			icon: <AddIcon />,
			title: "Nuovo ruolo",
			onClick: () => navigate("/settings/player-roles/new"),
		},
	];

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				current="Ruoli giocatori"
				actions={<MainActions actions={mainActions} />}
			/>
			<CardsContainer
				tableComponent={
					<CardsTable
						breadcrumbs
						configurationColumns={columns}
						data={playerRoles}
						fullWidth
						handleClick={(item) =>
							navigate("/settings/player-roles/" + item.id)
						}
						handleEdit={(item) =>
							navigate("/settings/player-roles/" + item.id + "/edit")
						}
						handleDelete={(item) => {
							setSelectedId(item.id);
							setOpenHandleConfirm(true);
						}}
						defaultSortedField="sorting"
					/>
				}
				listComponent={
					<CardsList
						type="users"
						breadcrumbs
						listConfiguration={listConfiguration}
						defaultSortedField="sorting"
					/>
				}
			/>
			<VLFModal
				open={openHandleConfirm}
				title="Eliminazione"
				content={
					<div className="mt-2">
						Sei sicuro di voler eliminare questo elemento?
					</div>
				}
				confirm={() => deletePlayerRole()}
				close={() => setOpenHandleConfirm(false)}
				confirmLabel="Conferma"
			/>
		</Page>
	);
}

export default SettingsPlayerRolesPage;
